/* Handling customer(user) state such as payment methods, billing info etc. */
import {
  GET_PAYMENT_METHOD_SUCCESS,
  GET_PAYMENT_METHODS_SUCCESS,
  STORE_PAYMENT_METHOD_SUCCESS,
  UPDATE_PAYMENT_METHOD_SUCCESS,
  CLEAR_STATE,
  STORE_PAYMENT_METHOD_STRIPE,
  SET_ACTIVE_PAYMENT_METHOD,
  SET_WITHOUT_PAYMENT_METHOD,
  CLEAR_PAYMENT_METHOD_ERROR,
  SET_DEFAULT_COUNTRY,
  GET_AMOUNT_TIERS_SUCCESS,
  UPDATE_CURRENCY,
  GET_SUBSCRIPTIONS
} from '@stores/mutation-types';
import {
  PaymentMethod,
  Customer
} from '@services/App/donation';
import {
  PAYMENT_GATEWAY,
  MODULES
} from '@constants';
import Forms from '@/form';
import moment from 'moment';

const state = () => ({
  /* Customer information */
  details: null,

  /* Set of payment methods saved in customer/user account */
  paymentMethods: [],

  /* Current payment method selected */
  selectedPaymentMethod: null,

  /* Active payment method selected */
  activePaymentMethod: null,

  /* If payment flow does not use a saved payment method */
  withoutPaymentMethod: null,

  /* Set of subscriptions the customer/user has */
  subscriptions: {
    loading: true,
    data: null,
    statistics: null
  },

  /* Billing information of customer for payment or saving/updating payment method */
  form: new Forms({
    billing: {
      name: null,
      email: null,
      address_line_1: null,
      address_line_2: null,
      city: null,
      state: null,
      country: null,
      postal_code: null
    },
    expiration_date: null
  }),

  // Customer settings
  settings: {
    group_key: 'payment-method'
  },

  // Customer responses
  responses: {
    stripeSave: null,
    serverSave: null,
    serverUpdate: null
  },

  amountTier: {
    loading: true,
    data: null
  }
});

const mutations = {

  /**
   * @param {object} state stripe module states
   * @param {string} payload List of payment methods
   *
   * @return void
   */
  [GET_PAYMENT_METHODS_SUCCESS]: (state, payload) => {
    state.paymentMethods = payload;
    state.withoutPaymentMethod = payload.length === 0;
  },

  /**
   * @param {object} state stripe module states
   * @param {string} payload Payment method information
   *
   * @return void
   */
  [GET_PAYMENT_METHOD_SUCCESS]: (state, payload) => {
    state.selectedPaymentMethod = payload;
    state.form.billing = {
      name: payload.card.name,
      address_line_1: payload.billing.address_line1,
      address_line_2: payload.billing.address_line2,
      city: payload.billing.city,
      state: payload.billing.state,
      country: payload.billing.country,
      postal_code: payload.billing.postal_code
    }
    state.form.expiration_date = moment(`${payload.card.expire_month}/${payload.card.expire_year}`, 'M/YYYY').format('MM/YY');
  },

  [SET_DEFAULT_COUNTRY]: (state, payload) => {
    state.form.billing.country = payload;
  },

  /**
   * @param {object} state stripe module states
   * @param {string} response Create payment response
   *
   * @return void
   */
  [STORE_PAYMENT_METHOD_STRIPE]: (state, response) => {
    state.responses.stripeSave = response;
  },

  /**
   * @param {object} state stripe module states
   * @param {string} response Create payment response
   *
   * @return void
   */
  [STORE_PAYMENT_METHOD_SUCCESS]: (state, response) => {
    state.responses.serverSave = response;
  },

  /**
   * @param {object} state stripe module states
   * @param {string} response Update payment response
   *
   * @return void
   */
  [UPDATE_PAYMENT_METHOD_SUCCESS]: (state, response) => {
    state.responses.serverUpdate = response;
  },

  /**
   * @param {object} state stripe module states
   * @param {string} methodId Id of the selected payment method
   *
   * @return void
   */
  [SET_ACTIVE_PAYMENT_METHOD]: (state, methodId) => {
    state.activePaymentMethod = methodId;
  },

  /**
   * @param {object} state stripe module states
   * @param {bool} If payment flow does or does not use payment method
   *
   * @return void
   */
  [SET_WITHOUT_PAYMENT_METHOD]: (state, withoutPaymentMethod) => {
    state.withoutPaymentMethod = withoutPaymentMethod;
  },

  /**
   * @param {object} state stripe module states
   * @param {bool} If payment flow does or does not use payment method
   *
   * @return void
   */
  [CLEAR_PAYMENT_METHOD_ERROR]: (state) => {
    state.responses.serverUpdate = null;
  },

  /**
   * @param {object} state stripe module states
   *
   * @return void
   */
  [CLEAR_STATE]: (state) => {
    state.form.billing = {
      name: null,
      email: null,
      address_line_1: null,
      address_line_2: null,
      city: null,
      state: null,
      country: null,
      postal_code: null
    };
    state.form.expiration_date = null;
  },

  /**
   * @param {object} state stripe module states
   * @param {string} payload Amount tiers information
   *
   * @return void
   */
  [GET_AMOUNT_TIERS_SUCCESS]: (state, payload) => {
    state.amountTier.loading = false;
    state.amountTier.data = payload;
  },

  /**
   * @param {object} state stripe module states
   * @param {string} payload subscription endpoint data
   *
   * @return void
   */
  [GET_SUBSCRIPTIONS]: (state, { loading, statistics, data }) => {
    state.subscriptions.loading = loading;
    state.subscriptions.data = data || null;
    state.subscriptions.statistics = statistics || null;
  },
};

const actions = {

  /**
   * Set the active payment method
   *
   * @param {object} context Vuex context keys
   * @param {string} methodId Id of the selected payment method
   *
   * @return {promise}
   */
  async setActivePaymentMethod({ commit }, methodId) {
    commit(SET_ACTIVE_PAYMENT_METHOD, methodId);
  },

  /**
   * Get list of payment methods
   *
   * @param {object} context Vuex context keys
   *
   * @return {promise}
   */
  async retrievePaymentMethods({ commit }) {
    const response = await PaymentMethod.getList();
    if (response.success) {
      commit(GET_PAYMENT_METHODS_SUCCESS, response.data.payment_methods);
    }
    return response;
  },

  /**
   * Get payment method details
   *
   * @param {object} context Vuex context keys
   * @param {string} methodId Id of the needed payment method
   *
   * @return {promise}
   */
  async retrievePaymentMethod({ state, commit, dispatch, rootState }, methodId) {

    const response = await PaymentMethod.get(methodId);
    if (!response.success) {
      return response;
    }

    let countries = rootState.donation.countries;
    if (countries.length <= 0) {
      await dispatch('donation/retrieveCountries', null, {
        root: true
      });
    }

    return commit(
      GET_PAYMENT_METHOD_SUCCESS,
      response.data.payment_methods[0]
    );
  },

  /**
   * Store payment method
   *
   * @param {object} context Vuex context keys
   * @param {object} payload Contains methods infromation.
   *
   * @return {promise}
   */
  async storePaymentMethod({ commit, dispatch }, payload) {
    const result = await PaymentMethod.setup(payload);

    if (!result.success) {
      return commit(STORE_PAYMENT_METHOD_SUCCESS, {
        success: false,
        type: 'setup',
        data: result.data || result.response
      });
    }

    const confirm = await dispatch(
      `${MODULES.STRIPE}/stripeConfirmSetupIntent`,
      {
        client_secret: result.data.client_secret
      },
      { root: true }
    );

    if (confirm.error) {
      return commit(STORE_PAYMENT_METHOD_SUCCESS, {
        success: false,
        type: 'confirm',
        data: confirm.error
      });
    }

    if (!payload.save_method_locally) {
      return commit(STORE_PAYMENT_METHOD_SUCCESS, {
        success: true,
        type: 'store',
        data: {}
      });
    }

    const store = await PaymentMethod.store(payload);

    commit(STORE_PAYMENT_METHOD_SUCCESS, {
      success: store.success,
      type: 'store',
      data: store.data || store.response
    });

    return store.success ? store.data : null;
  },

  /**
   * Update payment method
   *
   * @param {object} context Vuex context keys
   *
   * @return {promise}
   */
  async updatePaymentMethod({ state, getters: { billingUpdate }, commit }) {
    const paymentMethodId = state.selectedPaymentMethod.id;
    const { success, data } = await PaymentMethod.update(paymentMethodId, billingUpdate);
    return commit(UPDATE_PAYMENT_METHOD_SUCCESS, {
      success: success ? true : false,
      data
    });
  },

  /**
   * Delete payment method
   *
   * @param {object} context Vuex context keys
   * @param {string} methodId Id of the needed payment method
   *
   * @return {promise}
   */
  async deletePaymentMethod({ commit }, methodId) {
    const response = await PaymentMethod.delete(methodId);
    if (response.success) {
      commit(CLEAR_STATE);
    }
    return response;
  },

  /**
   * Create payment method
   *
   * @param {object} context Vuex context keys
   *
   * @return {promise}
   */
  async customerCreatePaymentMethod({ state, getters: { billingData }, commit, dispatch}, payload = {}) {
    const billing = payload.billingData || billingData;

    const result = await dispatch('stripe/stripeCreatePaymentMethod', {
      group: payload.group_key || state.settings.group_key,
      billing_details: billing
    }, {
      root: true
    });

    commit(STORE_PAYMENT_METHOD_STRIPE, {
      success: result.error ? false : true,
      data: result.error || result.paymentMethod
    });

    if (result.error) {
      throw result.error; // Just throw a result error if Stripe gives an error
    };

    return await dispatch('storePaymentMethod', {
      name: billing.name,
      payment_gateway: PAYMENT_GATEWAY.STRIPE,
      payment_gateway_payment_method_id: result.paymentMethod.id,
      save_method_locally: payload.saveMethod
    });
  },

  /**
   * Get customer amount tiers
   *
   * @param {object} context Vuex context keys
   *
   * @return {promise}
   */
  async retrieveAmountTiers({ commit, state }) {

    if (state.amountTier.data) {
      return state.amountTier;
    }

    const response = await Customer.getAmountTiers();

    if (response.success) {
      commit(GET_AMOUNT_TIERS_SUCCESS, response.data.tiers);
      // Also update the paymment currency
      commit(`payment/${UPDATE_CURRENCY}`, response.data.exchange, {
        root: true
      })
    }

    return state.amountTier;
  },

  /**
   * Get customer amount tiers
   *
   * @param {object} context Vuex context keys
   *
   * @return {promise}
   */
  async retrieveSubscriptions({ commit }) {
    commit(GET_SUBSCRIPTIONS, {
      loading: true
    });

    const response = await Customer.getSubscriptions();

    if (response.success) {
      commit(GET_SUBSCRIPTIONS, {
        loading: false,
        data: response.data,
        statistics: response.statistics
      });
      return;
    }

    commit(GET_SUBSCRIPTIONS, {
      loading: false
    });
  },

};

const getters = {

  /**
   * Object that will be used on save payment method endpoint
   *
   * @param {object} context Vuex context keys
   *
   * @return {object}
   */
  billingData: ({ form }) => {
    const { billing } = form.data();
    return {
      name: billing.name,
      address: {
        city: billing.city,
        country: billing.country,
        line1: billing.address_line_1,
        line2: billing.address_line_2,
        state: billing.state
      }
    }
  },

  /**
   * Object that will be used on update payment method endpoint
   *
   * @param {object} context Vuex context keys
   *
   * @return {object}
   */
  billingUpdate: ({ form }) => {
    const { billing, card } = form.data();
    const expiration = moment(form.expiration_date, 'MM/YY');
    return {
      billing: {
        address_line1: billing.address_line_1,
        address_line2: billing.address_line_2,
        city: billing.city,
        state: billing.state,
        country: billing.country,
        postal_code: billing.postal_code,
      },
      card: {
        name: billing.name,
        expire_month: parseInt(expiration.format('MM')),
        expire_year: expiration.format('YYYY')
      }
    }
  },

  /**
   * Active selected payment method that will be used on donation
   *
   * @param {object} context Vuex context keys
   *
   * @return {object}
   */
  activeMethod: ({ paymentMethods, activePaymentMethod, withoutPaymentMethod }) => {
    if (paymentMethods.length <= 0 || withoutPaymentMethod) {
      return null;
    }

    if (!activePaymentMethod) {
      return paymentMethods[0];
    }

    return paymentMethods.find(method => method.id === activePaymentMethod) || paymentMethods[0];
  },

  /**
   * Check on payment method save errors
   *
   * @param {object} context Vuex context keys
   *
   * @return {boolean}
   */
  isSaveError: ({ responses: { stripeSave, serverSave } }) => {
    if (stripeSave && !stripeSave.success) {
      return true;
    }
    if (serverSave && !serverSave.success) {
      return true;
    }
    if (stripeSave && serverSave) {
      return false;
    }
    return null;
  },

  /**
   * Get save error response
   *
   * @param {object} context Vuex context keys
   *
   * @return {boolean}
   */
  saveError: ({ responses: { stripeSave, serverSave } }) => {
    if (serverSave && !serverSave.success) {
      return serverSave.data[0];
    }
    return null;
  },

  /**
   * Check on payment method update errors
   *
   * @param {object} context Vuex context keys
   *
   * @return {boolean}
   */
  updatePaymentMethodError: ({ responses: { serverUpdate } }) => {
    if (serverUpdate && !serverUpdate.success) {
      return true;
    }
    if (serverUpdate) {
      return false;
    }
    return null;
  }

};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
